import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Copy, ShareIcon, TrashIcon, X } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { z } from 'zod';

import { useTrackShadowmap } from '@/api/shadowmap-requests';
import {
  getShadowmapSharesOptions,
  type ShadowmapShare,
  useGetShadowmapShares,
  useRevokeShadowmapShare,
  useShareShadowmap,
} from '@/api/shadowmap-share';
import { closeModal, openModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { queryClient } from '@/lib/react-query';

dayjs.extend(localizedFormat);

const createShadowmapShareSchema = z.object({
  expiresIn: z.string(),
});

type CreateShadowmapShareType = z.infer<typeof createShadowmapShareSchema>;

export interface ShareShadowmapModalProps {
  url: string;
  shadowmapId: string;
  tenantId: string;
  address?: string;
}

export const ShareShadowmapModal: React.FC<ShareShadowmapModalProps> = ({
  url,
  shadowmapId,
  tenantId,
  address,
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const posthog = usePostHog();
  const { data: shares } = useGetShadowmapShares({ shadowmapId, tenantId });
  const { mutate: trackShadowmap } = useTrackShadowmap();
  const displayedLinks: ShadowmapShare[] = (shares ?? []).filter(
    (l: ShadowmapShare) => !dayjs().isAfter(l.expiryDate)
  );

  const { mutate: shareShadowmap } = useShareShadowmap({
    mutationConfig: {
      onSuccess: (rows) => {
        const [{ token }] = rows;
        const link = new URL(url, window.location.origin);
        link.searchParams.set('token', token);
        const linkUrl = link.toString();
        navigator.clipboard.writeText(linkUrl);
        toast.success(t('copied-url'));
        posthog.capture?.('shadowmap_share_created', { shadowmapId });

        try {
          if (address) {
            trackShadowmap({ address, source: 'connect', tenantId });
          }
        } catch {
          // ignore tracking errors to not block UX
        }

        queryClient.invalidateQueries({ queryKey: ['shadowmap-requests'], exact: false });
        queryClient.invalidateQueries({
          queryKey: getShadowmapSharesOptions(shadowmapId, tenantId).queryKey,
        });
        setIsDropdownOpen(false);
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { mutateAsync: revokeShadowmapShare } = useRevokeShadowmapShare({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('successfully-revoked-link'));
        queryClient.invalidateQueries({
          queryKey: getShadowmapSharesOptions(shadowmapId, tenantId).queryKey,
        });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const handleCopy = async (linkUrl: string) => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      toast.success(t('copied-url'));
    } catch {
      toast.error(t('failed to copy'));
    }
  };

  return (
    <ModalBase className="w-[60vw] max-w-3xl">
      <X className="ml-auto cursor-pointer stroke-[4px] text-primary-light" onClick={closeModal} />
      <section className="mt-4 flex flex-row items-center justify-between">
        <h3 className="text-xl font-semibold">Links</h3>
        <DropdownMenu open={isDropdownOpen}>
          <DropdownMenuTrigger onClick={() => setIsDropdownOpen(true)} asChild>
            <Button>{t('createNewLink')}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="px-3 py-4">
            <ShareShadowmapPopup
              onShare={(values) => {
                shareShadowmap({
                  shadowmapId,
                  tenantId,
                  expiresIn: Number(values.expiresIn || '30'),
                });
              }}
              onCancel={() => setIsDropdownOpen(false)}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </section>
      {displayedLinks.length > 0 ? (
        <Table className="my-5 block max-h-[50vh] w-full table-auto bg-transparent">
          <TableHeader className="h-11 max-sm:hidden">
            <TableRow className="w-full justify-between">
              <TableHead className="text-center text-text-secondary">Link</TableHead>
              <TableHead className="text-center text-text-secondary">{t('created-at')}</TableHead>
              <TableHead className="text-center text-text-secondary">{t('expires-at')}</TableHead>
              <TableHead className="text-center text-text-secondary"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedLinks.map((l) => {
              const link = new URL(url, window.location.origin);
              link.searchParams.set('token', l.token);
              const linkUrl = link.toString();

              return (
                <TableRow key={l.token} className="items-center text-primary-dark">
                  <TableCell className="min-w-min py-5 text-center">
                    <a
                      className="block max-w-[250px] truncate no-underline"
                      href={linkUrl}
                      target="__blank"
                      rel="noreferrer"
                      title={linkUrl}
                      onClick={() => {
                        try {
                          posthog?.capture?.('shadowmap_share_link_accessed', { shadowmapId });
                        } catch {
                          // ignore tracking errors to not block UX
                        }
                      }}
                    >
                      {linkUrl}
                    </a>
                  </TableCell>

                  <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                    {dayjs(l.createdAt).format('DD.MM.YYYY')}
                  </TableCell>
                  <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                    {dayjs(l.expiryDate).format('DD.MM.YYYY')}
                  </TableCell>

                  <TableCell>
                    <div className="hidden w-auto min-w-32 items-center justify-end gap-1 py-5 text-center lg:grid lg:grid-cols-2 xl:flex">
                      <Button
                        onClick={() => handleCopy(linkUrl)}
                        variant="outline"
                        className="border-2"
                      >
                        <Copy aria-hidden />
                        <span className="sr-only">{t('copy')}</span>
                      </Button>
                      <Button
                        className="max-xl:max-w-[60px]"
                        onClick={() => {
                          openModal('confirm-delete-modal', {
                            title: t('deleteSharedLink'),
                            description: t('confirmDeleteSharedLink'),
                            onSubmit: async () => {
                              try {
                                await revokeShadowmapShare({ token: l.token });
                                openModal('share-shadowmap', {
                                  url,
                                  shadowmapId,
                                  tenantId,
                                  address,
                                });
                              } finally {
                                // modal re-open handled above
                              }
                            },
                            onClose: () =>
                              openModal('share-shadowmap', { url, shadowmapId, tenantId, address }),
                          });
                        }}
                        variant="destructive"
                      >
                        <TrashIcon aria-hidden />
                        <span className="sr-only"> {t('delete')}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="flex justify-center py-8">
          <span>{t('noDocuments')}</span>
        </div>
      )}
    </ModalBase>
  );
};

const ShareShadowmapPopup: React.FC<{
  onShare: (values: CreateShadowmapShareType) => void;
  onCancel: () => void;
}> = ({ onShare, onCancel }) => {
  const { t } = useTranslation();

  const methods = useForm<CreateShadowmapShareType>({
    resolver: zodResolver(createShadowmapShareSchema),
    defaultValues: {
      expiresIn: '30',
    },
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onShare)}>
        <div className="flex flex-col gap-3">
          <Controller
            name="expiresIn"
            control={methods.control}
            render={({ field, formState }) => (
              <Input
                {...field}
                type="number"
                className="mb-1"
                label={t('expires-in')}
                errors={[formState.errors.expiresIn?.message || '']}
              />
            )}
          />
        </div>
        <hr className="my-4" />
        <div className="flex flex-row gap-2">
          <Button variant="contained" type="submit" className="w-full">
            <ShareIcon className="mr-2 !h-4 !w-4" />
            {t('share')}
          </Button>
          <Button variant="outline" className="w-full" onClick={onCancel}>
            {t('close')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
